import { useCallback, useRef, useState } from 'react';
import {
  Flex,
  InputBox,
  PrimaryButton,
  StackScreen,
  useScreenSize,
} from '@banhala/ui-kit';
import { ApiError } from 'shared/src/api/httpClient';
import { CartIcon } from 'shared/src/component';
import { AnalyticsScreen } from 'shared/src/context';
import { useHandleBackPressed } from 'shared/src/hooks';
import { bridge } from 'shared/src/utils';
import { Countdown } from '../../components/Countdown';
import { useLogClickConfirmVerification } from '../../hooks/useLogClickConfirmVerification';
import { useLogClickRequestVerification } from '../../hooks/useLogClickRequestVerification';
import { useRecoveryIdResultPopup } from './components/useRecoveryIdResultPopup';
import { useLogViewFindId } from './hooks/useLogViewFindId';
import { useLogViewFoundId } from './hooks/useLogViewFoundId';
import { usePostRecoverEmail } from './queries/usePostRecoverEmail';
import { usePostValidateEmailAuth } from './queries/usePostValidateEmailAuth';

const PHONE_REGEX = /^01\d{8,9}$/;

const onlyDigits = (value: string) => value.replace(/[^0-9]/g, '');

const RecoveryIdContent = () => {
  const { width } = useScreenSize();
  const { showBackButton, onPressBack } = useHandleBackPressed();
  const { show: showResult } = useRecoveryIdResultPopup();
  const { mutateAsync: recoverEmail } = usePostRecoverEmail();
  const { mutateAsync: validateEmailAuth } = usePostValidateEmailAuth();

  const logClickRequestVerification = useLogClickRequestVerification();
  const logClickConfirmVerification = useLogClickConfirmVerification();
  const logViewFoundId = useLogViewFoundId();

  useLogViewFindId();

  const [phone, setPhone] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [publicCode, setPublicCode] = useState('');
  const [deadline, setDeadline] = useState(0);

  // 발급/검증 in-flight 중복 실행(더블탭) 차단.
  const busyRef = useRef(false);

  // 인증번호가 발급되면 검증 단계로 본다
  const waiting = publicCode !== '';
  const isPhoneValid = PHONE_REGEX.test(phone);

  const handlePhoneChange = useCallback(
    (value: string) => {
      const nextPhone = onlyDigits(value);
      if (nextPhone === phone) return;
      setPhone(nextPhone);
      if (publicCode !== '') {
        setPublicCode('');
        setAuthCode('');
        setDeadline(0);
      }
    },
    [phone, publicCode],
  );

  const handleAuthCodeChange = useCallback((value: string) => {
    setAuthCode(onlyDigits(value));
  }, []);

  const handleExpire = useCallback(() => {
    setPublicCode('');
    setDeadline(0);
  }, []);

  const handleIssue = useCallback(async () => {
    if (busyRef.current || !isPhoneValid) return;
    logClickRequestVerification();
    busyRef.current = true;
    try {
      const { public_code, remain_seconds } = await recoverEmail({
        mobile: phone,
      });
      setPublicCode(public_code);
      setDeadline(Date.now() + remain_seconds * 1000);
      setAuthCode('');
    } catch (error) {
      bridge.toast(
        error instanceof ApiError && error.message
          ? error.message
          : '인증번호 발급에 실패했어요',
      );
    } finally {
      busyRef.current = false;
    }
  }, [isPhoneValid, logClickRequestVerification, phone, recoverEmail]);

  const handleVerify = useCallback(async () => {
    if (busyRef.current || !waiting || authCode === '') return;
    logClickConfirmVerification();
    // 만료된 코드로의 검증 방어
    if (Date.now() >= deadline) return;
    busyRef.current = true;
    try {
      const { member_email } = await validateEmailAuth({
        public_code: publicCode,
        auth_code: authCode,
      });
      logViewFoundId();
      await showResult({ email: member_email });
      onPressBack();
    } catch (error) {
      bridge.toast(
        error instanceof ApiError && error.message
          ? error.message
          : '인증에 실패했어요',
      );
    } finally {
      busyRef.current = false;
    }
  }, [
    waiting,
    authCode,
    deadline,
    validateEmailAuth,
    publicCode,
    showResult,
    onPressBack,
    logClickConfirmVerification,
    logViewFoundId,
  ]);

  return (
    <StackScreen
      title={'아이디 찾기'}
      hideBackButton={!showBackButton}
      onPressBack={onPressBack}
      rightIcons={[<CartIcon key={'cart'} />]}
    >
      <Flex flex={1} width={width} alignSelf={'center'}>
        <Flex padding={{ horizontal: 16, top: 44 }} gap={20}>
          <Flex horizontal alignItems={'center'} gap={16}>
            <Flex flex={1}>
              <InputBox
                type={'underline'}
                placeholder={'휴대폰 번호 (숫자만 입력)'}
                value={phone}
                onChangeText={handlePhoneChange}
                keyboardType={'number-pad'}
              />
            </Flex>
            <PrimaryButton
              variant={'hug'}
              outline
              color={'gray'}
              size={'medium'}
              state={!isPhoneValid ? 'disabled' : undefined}
              onPress={handleIssue}
            >
              {waiting ? '다시 받기' : '인증번호'}
            </PrimaryButton>
          </Flex>

          {waiting && (
            <>
              <Flex gap={12}>
                <InputBox
                  type={'underline'}
                  placeholder={'인증번호 입력'}
                  value={authCode}
                  onChangeText={handleAuthCodeChange}
                  keyboardType={'number-pad'}
                />
                <Countdown deadline={deadline} onExpire={handleExpire} />
              </Flex>
              <PrimaryButton
                variant={'fill'}
                color={'gray'}
                size={'large'}
                state={authCode === '' ? 'disabled' : undefined}
                onPress={handleVerify}
              >
                {'확인'}
              </PrimaryButton>
            </>
          )}
        </Flex>
      </Flex>
    </StackScreen>
  );
};

export const RecoveryIdScreen = () => (
  <AnalyticsScreen screen={'FIND_ID'} hostsModals={false}>
    <RecoveryIdContent />
  </AnalyticsScreen>
);
